import React, { useContext, useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { FiEdit2, FiEye, FiFileText } from 'react-icons/fi'
import { format } from 'date-fns'

import { Container } from './styles'
import Card from '../../../../components/Card'
import avatarDefault from '../../../../assets/images/avatar.png'
import { api, authHeaders } from '../../../../services/api'
import { concatenarEndereco } from '../../../../util/endereco'
import Table from '../../../../components/Table'
import { downloadFileFromBlob, openTabSafari } from '../../../../util/file'
import { concatFormacoes } from '../../EspacoMedico/DadosCadastrais'
import Spinner from '../../../../components/Spinner'
import { GlobalContext } from '../../../../contexts/GlobalContext'
import { Button } from '../../../../components/Form'

export default function () {
	const { user, checkException } = useContext(GlobalContext)

	const { id } = useParams()
	const history = useHistory()

	const [pessoa, setPessoa] = useState(null)
	const [projetos, setProjetos] = useState(null)
	const [gerandoPDFPerfilTalento, setGerandoPDFPerfilTalento] = useState(false)

	async function loadPessoa() {
		try {
			const response = await api.get(`pessoa/${id}`, authHeaders())

			response.formacoes = response.formacoes.map(formacao => ({
				...formacao,
				instituicao: formacao.instituicao.id === 9999 ? `${formacao.faculdade_a_aprovar.descricao} (não validado)` : (formacao.instituicao?.sigla || formacao.instituicao.descricao)
			}))

			setPessoa(response)
		} catch (e) {
			checkException(e)
		}
	}

	async function loadProjetos() {
		try {
			const response = await api.get('projeto', {
				params: {
					pessoa_id: id
				},
				...authHeaders()
			})

			setProjetos(response.map(item => ({
				...item,
				inicio: format(new Date(item.inicio), 'dd/MM/yyyy'),
				fim: format(new Date(item.fim), 'dd/MM/yyyy'),
				condicao: item.projeto_pessoas.find(p => p.pessoa.id === pessoa.id)?.funcao.descricao
			})))
		} catch (e) {
			checkException(e)
		}
	}

	function handleAbrirDocumento(link) {
		openTabSafari(link)
	}

	async function handleGerarPDFPerfilTalento() {
		try {
			setGerandoPDFPerfilTalento(true)

			const response = await api.get(`talento/gerar_ficha/${id}`, {
				responseType: 'blob',
				...authHeaders()
			})

			downloadFileFromBlob(response, { extension: 'pdf' })
		} catch(e) {
			checkException(e)
		} finally {
			setGerandoPDFPerfilTalento(false)
		}
	}

	useEffect(() => {
		loadPessoa()
	}, [])

	useEffect(() => {
		if(pessoa) {
			loadProjetos()
		}
	}, [pessoa])

	if (!pessoa || !projetos) {
		return (
			<Container>
				<Card>
					<Spinner />
				</Card>
			</Container>
		)
	}

	const email = pessoa.contato.find(c => c.tipo_contato_id === 6)?.contato || 'não informado'
	const fone = pessoa.contato.find(c => c.tipo_contato_id !== 6)?.contato || 'não informado'

	return (
		<Container>
			<Card>
				<div className="grid grid-cols-[10rem_1fr_1fr_1fr] gap-4">
					<img src={avatarDefault} className="rounded-full row-span-3 w-36 h-36 object-cover" />

					<h1 className="col-span-2 mt-4">{pessoa.nome}</h1>

					<div className="flex flex-col items-end gap-4 mt-2">
						{!!id && (
							<Button 
								className="transparent w-fit justify-self-end" 
								loading={gerandoPDFPerfilTalento} 
								onClick={handleGerarPDFPerfilTalento}
							>
								Gerar PDF
								<FiFileText size={16} />
							</Button>
						)}

						{+id === user.pessoa.id && (
							<div className="p-2 hover:bg-white/10 hover:opacity-80 cursor-pointer flex gap-2 items-center w-fit justify-self-end" onClick={() => { history.push('/associado/dados_cadastrais') }}>
								Editar dados
								<FiEdit2 size={16} />
							</div>
						)}
					</div>

					<div className="border-b border-b-[#FFF5] pb-1 col-span-3 flex gap-3">
						<span className="font-semibold">Contatos:</span>
						<span>{email}</span>
						<span>{fone}</span>
					</div>

					<div className="col-span-3 flex gap-3">
						<span className="font-semibold">Endereço:</span>
						<span>{concatenarEndereco(pessoa.endereco)}</span>
					</div>

					<div className="col-span-4">
						<Table
							caption="Formações"
							headers={[
								{ name: 'descricao', value: 'Formação' },
								{ name: 'instituicao', value: 'Instituição', centered: true }
							]}
							data={pessoa.formacoes.map(formacao => ({
								id: formacao.id,
								certificado: formacao.certificado,
								descricao: `${formacao.grau.descricao} em ${concatFormacoes(formacao.formacao)}`,
								instituicao: formacao.instituicao || 'não informado'
  							}))}
							actions={[
								{
									action: item => {
										handleAbrirDocumento(item.certificado?.link)
									},
									checkDisabled: item => !item.certificado,
									disabledTooltip: 'Não anexado',
									icon: FiEye,
									iconSize: 16,
									name: 'Ver certificado'
								}
							]}
						/>
					</div>

					<div className="col-span-2">
						<Table
							caption="Documentos"
							headers={[
								{ name: 'arquivo', value: 'Arquivo' },
								{ name: 'numero', value: 'Número', centered: true }
							]}
							data={pessoa.documentos.map(documento => ({
								id: documento.id,
								arquivo: documento.tipo_documento.descricao,
								link: documento.arquivo?.link,
								numero: documento.identificador || '-'
							}))}
							actions={[
								{
									action: item => {
										handleAbrirDocumento(item.link)
									},
									checkDisabled: item => !item.link,
									disabledTooltip: 'Não anexado',
									icon: FiEye,
									iconSize: 16,
									name: 'Ver documento'
								}
							]}
						/>
					</div>

					<div className="col-span-2">
						<Table
							caption="Projetos"
							headers={[
								{ name: 'nome', value: 'Projeto' },
								{ name: 'condicao', value: 'Seu papel', centered: true },
								{ name: 'inicio', value: 'Início', centered: true },
								{ name: 'fim', value: 'Fim', centered: true }
							]}
							data={projetos}
							actions={[
								{
									action: item => {
										history.push(`/projeto/${item.id}` )
									},
									icon: FiEye,
									iconSize: 16,
									name: 'Ver projeto',
									checkDisabled: item => !item.projeto_pessoas.some(p => p.pessoa.id === user.pessoa.id),
									disabledTooltip: "Sem acesso"
								}
							]}
						/>
					</div>
				</div>
			</Card>
		</Container>
	)
}
